import React, { useEffect, useState } from 'react';
import { Button, Textarea } from '@tyndo/ui-kit';
import { useFocusTrap } from '@tyndo/hooks';
import { X, Save } from 'lucide-react';
import type { LayoutWidgetInstance, WidgetDefinition } from './types';

interface ConfigureDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: LayoutWidgetInstance | null;
  definition: WidgetDefinition | null;
  /** Optional widget-supplied custom form (e.g. PlantLayoutConfigForm). */
  customForm?: React.ComponentType<{
    value: Record<string, unknown>;
    onChange: (next: Record<string, unknown>) => void;
  }>;
  onSave: (instanceId: string, configJson: string) => void;
}

/**
 * Slide-in configure drawer for a single widget instance. Renders the
 * widget's `customForm` (federated `renderConfigure` slot) if exposed;
 * otherwise falls back to a generic JSON textarea so any widget remains
 * configurable.
 *
 * Accessibility:
 *   - `role="dialog"` + `aria-modal="true"` on the drawer container.
 *   - `useFocusTrap` traps Tab; Escape closes (and discards changes).
 *   - The drawer animates in from the right; respects `motion-reduce`.
 */
const ConfigureDrawer: React.FC<ConfigureDrawerProps> = ({
  open,
  onOpenChange,
  instance,
  definition,
  customForm: CustomForm,
  onSave,
}) => {
  const trapRef = useFocusTrap<HTMLDivElement>({
    active: open,
    onClose: () => onOpenChange(false),
  });
  // Initialise from the instance's existing configJson.
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [rawJson, setRawJson] = useState<string>('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (!instance) return;
    if (instance.configJson) {
      try {
        const parsed = JSON.parse(instance.configJson);
        setConfig(parsed && typeof parsed === 'object' ? parsed : {});
        setRawJson(instance.configJson);
        setJsonError(null);
      } catch {
        setConfig({});
        setRawJson(instance.configJson);
        setJsonError('Existing config is not valid JSON');
      }
    } else {
      setConfig({});
      setRawJson('{}');
      setJsonError(null);
    }
  }, [instance]);

  const handleSave = () => {
    if (!instance) return;
    let serialised: string;
    if (CustomForm) {
      serialised = JSON.stringify(config);
    } else {
      try {
        // Normalise + re-stringify to catch typos.
        const parsed = JSON.parse(rawJson || '{}');
        serialised = JSON.stringify(parsed);
        setJsonError(null);
      } catch {
        setJsonError('Cannot save — JSON is invalid');
        return;
      }
    }
    onSave(instance.instanceId, serialised);
    onOpenChange(false);
  };

  if (!open || !instance) return null;

  return (
    <>
      {/* Scrim */}
      <div
        className="fixed inset-0 z-40 bg-foreground/30 backdrop-blur-sm motion-reduce:transition-none"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />
      {/* Drawer */}
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Configure ${definition?.label ?? 'widget'}`}
        data-testid="configure-drawer"
        className="fixed inset-y-0 right-0 z-50 w-full sm:w-[420px] bg-surface-raised shadow-[var(--shadow-overlay)] flex flex-col animate-in slide-in-from-right motion-reduce:animate-none"
      >
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="min-w-0">
            <p className="t-eyebrow">Configure widget</p>
            <h2 className="text-sm font-semibold truncate" id="configure-drawer-title">
              {definition?.label ?? instance.widgetId}
            </h2>
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="h-auto w-auto whitespace-normal p-1 hover:bg-accent rounded transition-colors motion-reduce:transition-none"
            aria-label="Close configure drawer"
            data-testid="configure-drawer-close"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </header>

        <div className="flex-1 overflow-auto px-4 py-3">
          {CustomForm ? (
            <CustomForm value={config} onChange={setConfig} />
          ) : (
            <div className="flex flex-col gap-2">
              <label className="text-xs text-muted-foreground" htmlFor="configure-drawer-json">
                Widget configuration (JSON)
              </label>
              <Textarea
                id="configure-drawer-json"
                data-testid="configure-drawer-json"
                value={rawJson}
                onChange={(e) => setRawJson(e.target.value)}
                rows={14}
                className="w-full t-mono text-xs"
                spellCheck={false}
              />
              {jsonError && (
                <p className="text-xs text-alarm-critical" role="alert">
                  {jsonError}
                </p>
              )}
            </div>
          )}
        </div>

        <footer className="border-t border-border px-4 py-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} size="sm">
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            size="sm"
            data-testid="configure-drawer-save"
          >
            <Save className="h-3 w-3 mr-1" aria-hidden="true" />
            Save changes
          </Button>
        </footer>
      </div>
    </>
  );
};

export default ConfigureDrawer;
